/**
 * GET /admin/auth/me permissions 트리 → 사이드바 항목.
 * menuKey는 서버 난수일 수 있으므로 urlPath로 앱 라우트에 연결한다.
 * 계층·displayOrder·visible·permissions(menuKey)는 그대로 사용한다.
 *
 * 아이콘은 sidebarMenuConfig 카탈로그(urlPath)에서만 신뢰한다.
 * 카탈로그 매칭이 전무하면 null → AdminLayout이 하드코딩 사이드바로 폴백.
 */
import type { MenuPermission } from '@/entities/auth/api/auth.api';
import type { AdminMenuTreeNode } from '@/entities/menu/types';
import { hasMenuPermission } from '@/shared/utils/authPermissions';
import { isFullAccessRole } from '@/shared/utils/authRoles';
import {
  isSettingsSidebarUrl,
  resolveGroupIcon,
  resolveRouteByUrlPath,
} from './menuUrlRouteMap';
import type { MenuGroup, MenuLink, SidebarMenuItem } from './sidebarMenuConfig';

const byDisplayOrder = (a: AdminMenuTreeNode, b: AdminMenuTreeNode) =>
  a.displayOrder - b.displayOrder;

const canAccessMenu = (
  menuKey: string,
  permissions: MenuPermission[] | undefined | null,
  role?: string | null,
): boolean => {
  if (isFullAccessRole(role)) return true;
  return hasMenuPermission(permissions, menuKey, 'READ');
};

/**
 * 설정 그룹 — 설정 URL 자체이거나,
 * 자식 중 설정 URL이 있고 일반(비설정) URL 자식이 없을 때.
 */
const isSettingsTreeNode = (node: AdminMenuTreeNode): boolean => {
  if (isSettingsSidebarUrl(node.urlPath)) return true;
  const kids = (node.children ?? []).filter((c) => c.visible);
  const withUrl = kids.filter((c) => Boolean(c.urlPath?.trim()));
  if (withUrl.length === 0) return false;
  const hasSettings = withUrl.some((c) => isSettingsSidebarUrl(c.urlPath));
  const hasAppRoute = withUrl.some((c) => !isSettingsSidebarUrl(c.urlPath));
  return hasSettings && !hasAppRoute;
};

const toMenuLink = (
  node: AdminMenuTreeNode,
  permissions: MenuPermission[] | undefined | null,
  role?: string | null,
): (MenuLink & { fromCatalog: boolean }) | null => {
  const route = resolveRouteByUrlPath(node.urlPath);
  if (!route) return null;
  if (isSettingsSidebarUrl(route.path)) return null;
  if (!canAccessMenu(node.menuKey, permissions, role)) return null;
  return {
    key: route.path,
    label: node.menuName || route.label,
    icon: route.icon,
    menuCode: node.menuKey,
    fromCatalog: route.fromCatalog,
  };
};

/**
 * 서버 메뉴 트리로 사이드바를 재구성한다.
 * - SETTINGS(설정 URL 그룹)는 메인에서 제외
 * - 리프/링크는 urlPath → 기존 사이드바 아이콘 카탈로그
 * - 카탈로그 아이콘이 하나도 없으면 null (하드코딩 폴백)
 */
export const buildSidebarFromMenuTree = (
  menus: AdminMenuTreeNode[] | undefined | null,
  options: {
    permissions?: MenuPermission[] | undefined | null;
    role?: string | null;
  } = {},
): SidebarMenuItem[] | null => {
  if (!menus?.length) return null;

  const { permissions, role } = options;
  const items: SidebarMenuItem[] = [];
  let catalogHits = 0;

  for (const node of [...menus].sort(byDisplayOrder)) {
    if (!node.visible) continue;
    if (isSettingsTreeNode(node)) continue;

    const childLinks = [...(node.children ?? [])]
      .sort(byDisplayOrder)
      .flatMap((child) => {
        if (!child.visible) return [];
        const link = toMenuLink(child, permissions, role);
        return link ? [link] : [];
      });

    if (childLinks.length > 0) {
      catalogHits += childLinks.filter((l) => l.fromCatalog).length;
      const group: MenuGroup = {
        label: node.menuName || node.menuKey,
        icon: resolveGroupIcon(
          node.urlPath,
          childLinks.map((link) => link.key),
        ),
        children: childLinks.map(({ fromCatalog: _fc, ...link }) => link),
      };
      items.push(group);
      continue;
    }

    const selfLink = toMenuLink(node, permissions, role);
    if (selfLink) {
      const { fromCatalog, ...link } = selfLink;
      if (fromCatalog) catalogHits += 1;
      items.push(link);
    }
  }

  if (items.length === 0) return null;
  // urlPath가 카탈로그와 전혀 안 맞으면 전부 Menu(햄버거) 아이콘이 된다.
  // 이 경우 기존 하드코딩 사이드바(아이콘 SSOT)로 넘긴다.
  if (catalogHits === 0) return null;

  return items;
};
